import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Pagination } from '@components/molecules/Pagination';
import { useGetSpaces, GetSpacesInput, SpaceItem } from '@services/useGetSpaces';
import { useDeleteSpace } from '@services/useDeleteSpace';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { SITE_URLS } from '@constants';
import { ResultsType } from '@types';

export interface SpacesTableProps {
    filters?: GetSpacesInput;
    perPage?: number;
}

export const SpacesTable: FC<SpacesTableProps> = ({ filters = {}, perPage = 10 }) => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const { spaces, isLoadingSpaces } = useData();
    const { request: fetchSpaces } = useGetSpaces({ own: true });
    const {
        request: deleteSpace,
        success: deleteSuccess,
        errorMessage: deleteError,
        lastResponseTime: deleteLastResponseTime,
    } = useDeleteSpace();

    const [currentPage, setCurrentPage] = useState<number>(1);

    const translations = {
        id: t('spaces-table.columns.id'),
        title: t('spaces-table.columns.title'),
        type: t('spaces-table.columns.type'),
        status: t('spaces-table.columns.status'),
        price: t('spaces-table.columns.price'),
        noSpaces: t('spaces-table.no-spaces'),
        deleteSuccess: t('spaces-table.delete.success'),
        deleteConfirm: t('spaces-table.delete.confirm'),
        loading: t('spaces-table.loading'),
    };

    // Fetch spaces on mount and when filters/page change
    useEffect(() => {
        fetchSpaces({
            ...filters,
            page: currentPage,
            perPage: perPage,
            orderBy: 'price',
            sortDesc: true,
        }).then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, filters, perPage]);

    // Handle delete response
    useEffect(() => {
        if (deleteLastResponseTime) {
            if (deleteSuccess) {
                showDefaultNotification({ type: 'success', message: translations.deleteSuccess });
                // Refresh the list
                fetchSpaces({
                    ...filters,
                    page: currentPage,
                    perPage: perPage,
                }).then();
            } else if (deleteError) {
                showDefaultNotification({ type: 'error', message: deleteError });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteLastResponseTime]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleDelete = (uuid: string) => {
        if (uuid) {
            const confirmed = window.confirm(translations.deleteConfirm);
            if (confirmed) {
                deleteSpace(uuid);
            }
        }
    };

    const spacesList = spaces?.data || [];
    const totalRecords = spaces?.recordsTotal || 0;
    const totalPages = Math.ceil(totalRecords / perPage);

    const formatPrice = (price: string | number) => {
        const numeric =
            typeof price === 'number'
                ? price
                : parseFloat(price.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.'));
        return new Intl.NumberFormat('pt-PT', {
            style: 'currency',
            currency: 'EUR',
        }).format(numeric);
    };

    const truncateUuid = (uuid: string) => {
        if (uuid.length <= 8) return uuid;
        return `${uuid.substring(0, 8)}...`;
    };

    if (isLoadingSpaces) {
        return (
            <Card classes="mb-0">
                <Typography color="muted">{translations.loading}</Typography>
            </Card>
        );
    }

    if (spacesList.length === 0) {
        return (
            <Card classes="mb-0 mt-4">
                <Typography color="muted">{translations.noSpaces}</Typography>
            </Card>
        );
    }

    return (
        <>
            <Card classes="mb-0 mt-4">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-left', text: translations.id },
                        { classes: 'text-left', text: translations.title },
                        { classes: 'text-center', hideOnMobile: true, text: translations.type },
                        { classes: 'text-center', hideOnMobile: true, text: translations.status },
                        { classes: 'text-center', text: translations.price },
                        { classes: 'text-left', text: '' },
                    ]}
                    contentRows={spacesList.map((space: SpaceItem) => {
                        return [
                            {
                                classes: 'text-left',
                                content: (
                                    <span title={space.uuid}>
                                        <Typography fontSize="14">{truncateUuid(space.uuid)}</Typography>
                                    </span>
                                ),
                            },
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{space.title}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{space.spaceType?.name || '-'}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{space.spaceStatus?.name || '-'}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatPrice(space.price)}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key="edit"
                                                href={SITE_URLS.editSpace.replace(':uuid', space.uuid)}
                                                mode={AnchorMode.none}
                                            >
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-pen-to-square" />
                                                </Typography>
                                            </Anchor>,
                                            <button key="delete" onClick={() => handleDelete(space.uuid)}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-trash-can" />
                                                </Typography>
                                            </button>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>

            <Pagination
                loading={isLoadingSpaces}
                type={ResultsType.spaces}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </>
    );
};
